function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
/* SANS: open header dropdown on hover */
document.addEventListener('DOMContentLoaded', function () {
  const menuItems = document.querySelectorAll('.header__inline-menu details');

  menuItems.forEach((details) => {
    details.addEventListener('mouseenter', () => {
      details.setAttribute('open', '');
    });

    details.addEventListener('mouseleave', () => {
      details.removeAttribute('open');
    });
  });
});
/* SANS: open localization dropdown on hover */
document.addEventListener('DOMContentLoaded', function () {
  const localizationDetails = document.querySelectorAll(
    '.desktop-localization-wrapper details, .localization-form details'
  );

  localizationDetails.forEach((details) => {
    details.addEventListener('mouseenter', function () {
      details.setAttribute('open', '');
    });

    details.addEventListener('mouseleave', function () {
      details.removeAttribute('open');
    });
  });
});
/* SANS: force open language selector on hover */
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.desktop-localization-wrapper localization-form').forEach(function (form) {
    const details = form.querySelector('details');
    if (!details) return;

    form.addEventListener('mouseenter', function () {
      details.setAttribute('open', '');
    });

    form.addEventListener('mouseleave', function () {
      details.removeAttribute('open');
    });
  });
});

/* SANS: customer reviews final 80s loop */
(function () {
  function initReviewsCarousel(root) {
    if (!root || root.dataset.sansReviewsLoopReady === 'true') return;

    const track = root.querySelector('[data-reviews-track], [class*="ai-reviews-grid"]');
    const cards = Array.from(root.querySelectorAll('[data-review-card]:not([data-review-clone]), [class*="ai-reviews-card"]:not([data-review-clone])'));
    if (!track || cards.length < 2) return;

    root.dataset.sansReviewsLoopReady = 'true';
    root.dataset.sansReviewsCarouselReady = 'true';

    let loopWidth = 0;
    let lastFrame = 0;
    let manualUntil = 0;
    let isDragging = false;
    let hasDragged = false;
    let suppressClick = false;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let lastDragX = 0;
    let lastDragTime = 0;
    let dragVelocity = 0;
    let glideFrame = 0;
    const dragSensitivity = 1.22;

    function ensureClones() {
      if (track.querySelector('[data-review-clone="true"]')) return;

      cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-review-clone', 'true');
        track.appendChild(clone);
      });
    }

    function measureLoop() {
      ensureClones();
      const firstClone = track.querySelector('[data-review-clone="true"]');
      loopWidth = firstClone ? firstClone.offsetLeft : Math.max(0, track.scrollWidth / 2);
      return loopWidth;
    }

    function normalizeScroll() {
      const width = loopWidth || measureLoop();
      if (width <= 0) return;
      while (track.scrollLeft >= width) track.scrollLeft -= width;
      while (track.scrollLeft < 0) track.scrollLeft += width;
    }

    function getStep() {
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return (cards[0]?.getBoundingClientRect().width || 288) + gap;
    }

    function glideBy(distance) {
      if (glideFrame) {
        window.cancelAnimationFrame(glideFrame);
        glideFrame = 0;
      }

      const start = track.scrollLeft;
      const duration = 460;
      const started = performance.now();

      function animate(time) {
        const progress = Math.min(1, (time - started) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        track.scrollLeft = start + distance * eased;
        normalizeScroll();

        if (progress < 1 && !isDragging) {
          glideFrame = window.requestAnimationFrame(animate);
        } else {
          glideFrame = 0;
        }
      }

      glideFrame = window.requestAnimationFrame(animate);
    }

    function setInitialOffset() {
      if (track.scrollLeft > 2) return;

      const viewport = window.innerWidth || document.documentElement.clientWidth || 0;
      const reveal = viewport < 750 ? 36 : 144;
      track.scrollLeft = Math.max(0, getStep() - reveal);
      normalizeScroll();
    }

    function move(direction) {
      measureLoop();
      normalizeScroll();
      if (direction < 0 && track.scrollLeft <= 2 && loopWidth > 0) {
        track.scrollLeft = loopWidth;
      }

      const start = track.scrollLeft;
      const target = start + getStep() * direction;
      const duration = 220;
      const started = performance.now();
      manualUntil = started + duration + 180;

      function animate(time) {
        const progress = Math.min(1, (time - started) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        track.scrollLeft = start + (target - start) * eased;
        normalizeScroll();
        if (progress < 1) {
          window.requestAnimationFrame(animate);
        }
      }

      window.requestAnimationFrame(animate);
    }

    function pauseAuto(ms) {
      manualUntil = Math.max(manualUntil, performance.now() + ms);
    }

    function beginDrag(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      measureLoop();
      normalizeScroll();
      if (loopWidth > 0 && track.scrollLeft <= 2) {
        track.scrollLeft = loopWidth;
      }

      isDragging = true;
      hasDragged = false;
      dragVelocity = 0;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      lastDragX = event.clientX;
      lastDragTime = performance.now();
      manualUntil = Number.POSITIVE_INFINITY;
      root.classList.add('sans-reviews-is-dragging');
      if (glideFrame) {
        window.cancelAnimationFrame(glideFrame);
        glideFrame = 0;
      }

      try {
        track.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is not available in every embedded browser.
      }
    }

    function drag(event) {
      if (!isDragging) return;

      const deltaX = event.clientX - dragStartX;
      if (Math.abs(deltaX) > 3) {
        hasDragged = true;
        event.preventDefault();
      }

      const now = performance.now();
      const deltaTime = Math.max(16, now - lastDragTime);
      dragVelocity = (event.clientX - lastDragX) / deltaTime;
      lastDragX = event.clientX;
      lastDragTime = now;

      track.scrollLeft = dragStartScroll - deltaX * dragSensitivity;
      normalizeScroll();
    }

    function endDrag(event) {
      if (!isDragging) return;

      isDragging = false;
      root.classList.remove('sans-reviews-is-dragging');

      try {
        track.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Ignore release failures from cancelled pointer streams.
      }

      if (hasDragged) {
        const momentum = Math.max(-520, Math.min(520, -dragVelocity * 520));
        if (Math.abs(momentum) > 8) {
          glideBy(momentum);
        }
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 0);
      }

      manualUntil = performance.now() + 1400;
    }

    track.addEventListener('pointerdown', beginDrag);
    track.addEventListener('pointermove', drag);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    root.querySelectorAll('[data-reviews-nav], [class*="ai-reviews-arrow"]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const className = String(button.className || '');
        const direction = button.dataset.reviewsNav === 'next' || className.includes('next') ? 1 : -1;
        move(direction);
      });
    });

    function tick(time) {
      if (!lastFrame) lastFrame = time;
      const delta = Math.min(64, time - lastFrame);
      lastFrame = time;

      measureLoop();
      if (loopWidth > 0 && !isDragging && document.visibilityState !== 'hidden' && time > manualUntil) {
        track.scrollLeft += (loopWidth / 80000) * delta;
        normalizeScroll();
      }

      window.requestAnimationFrame(tick);
    }

    measureLoop();
    window.setTimeout(() => {
      measureLoop();
      setInitialOffset();
    }, 80);

    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        measureLoop();
        normalizeScroll();
      }).observe(track);
    } else {
      window.addEventListener('resize', measureLoop, { passive: true });
    }

    window.requestAnimationFrame(tick);
  }

  function initAllReviewsCarousels(scope) {
    const target = scope || document;
    target.querySelectorAll('[data-sans-reviews-root], [class*="ai-reviews-section"]').forEach(initReviewsCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllReviewsCarousels(document), { once: true });
  } else {
    initAllReviewsCarousels(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAllReviewsCarousels(event.target));
})();

(() => {
  const SECTION_ID = 'shopify-section-template--18917941411888__slideshow';

  function bindSansHeroControls(root) {
    const section = (root || document).querySelector(`#${SECTION_ID}`);
    if (!section || section.dataset.sansHeroGlobalControls === 'true') return;

    section.dataset.sansHeroGlobalControls = 'true';
    section.querySelectorAll('[data-sans-hero-control]').forEach((control) => {
      control.addEventListener('click', () => {
        const direction = control.getAttribute('data-sans-hero-control');
        const nativeButton = section.querySelector(
          direction === 'previous' ? '.slider-button--prev' : '.slider-button--next'
        );

        nativeButton?.click();
      });
    });
  }

  async function refreshSansHeroFromSectionRender() {
    const section = document.getElementById(SECTION_ID);
    if (section?.querySelector('.sans-hero-fixed-card, [data-sans-hero-motion]')) {
      bindSansHeroControls(document);
      return;
    }

    if (!section || section.querySelector('.sans-hero-claims')) {
      bindSansHeroControls(document);
      return;
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', 'template--18917941411888__slideshow');
      url.searchParams.set('_sans_section_refresh', Date.now().toString());

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshSection = doc.getElementById(SECTION_ID);

      if (!freshSection || !freshSection.querySelector('.sans-hero-claims')) return;

      section.replaceWith(freshSection);
      bindSansHeroControls(document);
    } catch (error) {
      // Keep the existing hero if Shopify section rendering is temporarily unavailable.
    }
  }

  function initSansHeroRefresh() {
    refreshSansHeroFromSectionRender();
    window.setTimeout(refreshSansHeroFromSectionRender, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSansHeroRefresh, { once: true });
  } else {
    initSansHeroRefresh();
  }

  document.addEventListener('shopify:section:load', () => bindSansHeroControls(document));
})();

(() => {
  const bad = String.fromCharCode(65533);
  const star = String.fromCharCode(9733);
  const skipTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT']);

  function cleanSansText(value) {
    if (!value || (!value.includes(bad) && !value.includes('?'))) return value;

    if (value.includes('96% prefer SANS over regular coffee') && value.includes(bad)) {
      return `${star}${star}${star}${star}${star} 96% prefer SANS over regular coffee`;
    }

    let next = value;
    const pairs = ['?' + bad, bad + '?'];

    pairs.forEach((marker) => {
      next = next
        .split(marker + 's').join("'s")
        .split(marker + 'so').join(', so')
        .split(marker + 'from').join(', from');
    });

    next = next
      .split('?' + bad).join('')
      .split(bad + '?').join('')
      .split(bad).join('');

    return next;
  }

  function fixSansBrokenText(scope = document) {
    const root = scope.nodeType === Node.ELEMENT_NODE ? scope : document.body;
    if (!root) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || skipTags.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        return node.nodeValue && (node.nodeValue.includes(bad) || node.nodeValue.includes('?'))
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const cleaned = cleanSansText(node.nodeValue);
      if (cleaned !== node.nodeValue) node.nodeValue = cleaned;
    });
  }

  function bootSansBrokenTextFix(scope = document) {
    fixSansBrokenText(scope);
    window.setTimeout(() => fixSansBrokenText(scope), 100);
    window.setTimeout(() => fixSansBrokenText(scope), 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootSansBrokenTextFix(document), { once: true });
  } else {
    bootSansBrokenTextFix(document);
  }

  document.addEventListener('shopify:section:load', (event) => bootSansBrokenTextFix(event.target));

  if ('MutationObserver' in window) {
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        fixSansBrokenText(document);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();

(() => {
  const successSelectors = [
    '[data-header-newsletter-success]',
    '[data-sans-footer-newsletter-success]',
    '[data-sans-reminder-message]',
    '.newsletter-form__message--success',
  ].join(',');

  const hideNewsletterSuccess = (message) => {
    if (!message || !message.matches?.(successSelectors) || message.hidden) return;

    window.clearTimeout(message.__sansNewsletterHideTimer);
    message.__sansNewsletterHideTimer = window.setTimeout(() => {
      message.hidden = true;
      const form = message.closest('form');
      form?.classList.remove('is-success');
    }, 3000);
  };

  const initNewsletterSuccessTimers = (scope = document) => {
    scope.querySelectorAll?.(successSelectors).forEach(hideNewsletterSuccess);
    if (scope.matches?.(successSelectors)) hideNewsletterSuccess(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initNewsletterSuccessTimers(document), { once: true });
  } else {
    initNewsletterSuccessTimers(document);
  }

  document.addEventListener('shopify:section:load', (event) => initNewsletterSuccessTimers(event.target));

  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
          hideNewsletterSuccess(mutation.target);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initNewsletterSuccessTimers(node);
        });
      });
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['hidden'],
      childList: true,
      subtree: true,
    });
  }
})();

/* SANS reviews: 96 percent count-up motion */
(() => {
  function initSansReviewsPercentCounter(scope) {
    const target = scope || document;

    target.querySelectorAll('[data-sans-reviews-root]').forEach((root) => {
      if (root.dataset.sansReviewsCounterReady === 'true') return;

      const percent = root.querySelector('[data-sans-reviews-percent]');
      if (!percent) return;

      const rawValue = percent.textContent.trim();
      const numberMatch = rawValue.match(/-?\d+(?:\.\d+)?/);
      if (!numberMatch) return;

      const endValue = Number(numberMatch[0]);
      const prefix = rawValue.slice(0, numberMatch.index);
      const suffix = rawValue.slice(numberMatch.index + numberMatch[0].length) || '%';
      const duration = 1400;

      root.dataset.sansReviewsCounterReady = 'true';
      percent.textContent = `${prefix}0${suffix}`;

      const render = (value) => {
        percent.textContent = `${prefix}${Math.round(value)}${suffix}`;
      };

      const run = () => {
        if (root.dataset.sansReviewsCounterDone === 'true') return;
        root.dataset.sansReviewsCounterDone = 'true';

        const started = performance.now();

        const tick = (time) => {
          const progress = Math.min(1, (time - started) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          render(endValue * eased);

          if (progress < 1) {
            window.requestAnimationFrame(tick);
          } else {
            render(endValue);
          }
        };

        window.requestAnimationFrame(tick);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            run();
          });
        }, { threshold: 0.35 });

        observer.observe(root);
      } else {
        run();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSansReviewsPercentCounter(document), { once: true });
  } else {
    initSansReviewsPercentCounter(document);
  }

  document.addEventListener('shopify:section:load', (event) => initSansReviewsPercentCounter(event.target));

  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          initSansReviewsPercentCounter(document);
          break;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();

/* SANS reviews: runtime inline Figma lock for node 559:6593 */
(() => {
  const important = (element, styles) => {
    if (!element) return;
    Object.entries(styles).forEach(([property, value]) => {
      element.style.setProperty(property, value, 'important');
    });
  };

  const reviewTextStyles = {
    color: '#303030',
    'font-family': 'Inter, sans-serif',
    'font-size': '18px',
    'font-weight': '700',
    'letter-spacing': '-0.72px',
    'line-height': '28px',
    flex: '0 0 56px',
    height: '56px',
    'min-height': '56px',
    'max-height': '56px',
    margin: '0',
    padding: '0',
    overflow: 'hidden',
    'text-align': 'center',
    width: '240px',
    'max-width': '100%',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '2',
    'white-space': 'normal',
    'box-sizing': 'border-box',
  };

  const reviewCardBaseShadow = '0 12px 28px rgba(19,78,74,0.12)';
  const reviewCardLiftShadow = '0 18px 28px -22px rgba(19,78,74,0.36), 0 8px 14px -12px rgba(19,78,74,0.22)';

  const isReviewCardLifted = (card) =>
    card?.dataset.sansReviewCardLifted === 'true' ||
    card?.matches(':hover') ||
    card?.matches(':focus-within');

  const applyReviewCardLift = (card, lifted) => {
    if (!card) return;
    card.dataset.sansReviewCardLifted = lifted ? 'true' : 'false';
    important(card, {
      transform: lifted ? 'translateY(-8px)' : 'translateY(0)',
      'box-shadow': lifted ? reviewCardLiftShadow : reviewCardBaseShadow,
      'z-index': lifted ? '3' : 'auto',
    });
  };

  const bindReviewCardLift = (root) => {
    if (!root || root.dataset.sansReviewCardLiftReady === 'true') return;
    root.dataset.sansReviewCardLiftReady = 'true';

    root.addEventListener('pointerover', (event) => {
      const card = event.target.closest('[data-review-card], article[class*="ai-reviews-card"]');
      if (!card || !root.contains(card)) return;
      applyReviewCardLift(card, true);
    });

    root.addEventListener('pointerout', (event) => {
      const card = event.target.closest('[data-review-card], article[class*="ai-reviews-card"]');
      if (!card || !root.contains(card) || card.contains(event.relatedTarget)) return;
      applyReviewCardLift(card, false);
    });

    root.addEventListener('focusin', (event) => {
      const card = event.target.closest('[data-review-card], article[class*="ai-reviews-card"]');
      if (!card || !root.contains(card)) return;
      applyReviewCardLift(card, true);
    });

    root.addEventListener('focusout', (event) => {
      const card = event.target.closest('[data-review-card], article[class*="ai-reviews-card"]');
      if (!card || !root.contains(card) || card.contains(event.relatedTarget)) return;
      applyReviewCardLift(card, false);
    });
  };

  function applySansReviewFigmaInlineLock(scope) {
    const target = scope || document;
    const isMobileReviewLayout = window.matchMedia('(max-width: 767.98px)').matches;
    target.querySelectorAll('[data-sans-reviews-root]').forEach((root) => {
      bindReviewCardLift(root);

      important(root, {
        display: 'flex',
        'flex-direction': 'column',
        'align-items': 'center',
        'justify-content': 'center',
        width: '100%',
        'min-height': isMobileReviewLayout ? '748px' : '950px',
        margin: '0',
        padding: isMobileReviewLayout ? '40px 0' : '80px 40px 40px',
        background: '#c0ffee',
        color: '#303030',
        overflow: 'hidden',
        'box-sizing': 'border-box',
      });

      const container = root.querySelector('[class*="ai-reviews-container"]');
      important(container, {
        display: 'flex',
        'flex-direction': 'column',
        'align-items': 'center',
        'justify-content': 'center',
        gap: '40px',
        width: '100%',
        height: '830px',
        'min-height': '830px',
        'max-width': '1200px',
        'min-width': '328px',
        margin: '0 auto',
        padding: '0',
        'box-sizing': 'border-box',
      });

      const header = root.querySelector('[class*="ai-reviews-header"]');
      const copy = root.querySelector('[class*="ai-reviews-copy"]');
      const titleRow = root.querySelector('[class*="ai-reviews-title-row"]');
      [header, copy, titleRow].forEach((element) => {
        important(element, {
          display: 'flex',
          'flex-direction': 'column',
          'align-items': 'center',
          'justify-content': 'flex-start',
          gap: '0',
          width: '100%',
          height: '202px',
          'min-height': '202px',
          margin: '0',
          padding: '0',
          'text-align': 'center',
          'box-sizing': 'border-box',
        });
      });

      const statGroup = root.querySelector('[class*="ai-reviews-stat-group"]');
      important(statGroup, {
        display: 'flex',
        flex: '0 0 126px',
        'flex-direction': 'column',
        'align-items': 'center',
        'justify-content': 'flex-start',
        gap: '0',
        width: '100%',
        height: '126px',
        'min-height': '126px',
        margin: '0',
        padding: '0',
        background: 'transparent',
        'box-shadow': 'none',
        filter: 'none',
        overflow: 'visible',
        'box-sizing': 'border-box',
      });

      const heroStars = root.querySelector('[class*="ai-reviews-stat-stars"]');
      important(heroStars, {
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        'flex-flow': 'row nowrap',
        gap: '0',
        width: '200px',
        'min-width': '200px',
        height: '40px',
        'min-height': '40px',
        margin: '0 auto',
        padding: '0',
        color: '#ffbf00',
        'line-height': '0',
        background: 'transparent',
        'box-shadow': 'none',
        filter: 'none',
      });
      heroStars?.querySelectorAll('svg').forEach((svg) => {
        important(svg, {
          display: 'block',
          flex: '0 0 40px',
          width: '40px',
          height: '40px',
          padding: '5px',
          fill: '#ffbf00',
          background: 'transparent',
          'box-shadow': 'none',
          filter: 'none',
          'box-sizing': 'border-box',
        });
      });
      heroStars?.querySelectorAll('img').forEach((img) => {
        important(img, {
          display: 'block',
          flex: '0 0 200px',
          width: '200px',
          height: '40px',
          margin: '0',
          padding: '0',
          border: '0',
          background: 'transparent',
          'box-shadow': 'none',
          filter: 'none',
          'pointer-events': 'none',
          'box-sizing': 'border-box',
        });
      });

      const percent = root.querySelector('[data-sans-reviews-percent]');
      important(percent, {
        display: 'block',
        flex: '0 0 86px',
        color: '#134e4a',
        'font-family': 'Inter, sans-serif',
        'font-size': '60px',
        'font-weight': '700',
        'letter-spacing': '-3px',
        'line-height': '86px',
        height: '86px',
        'min-height': '86px',
        margin: '0 auto',
        padding: '0',
        'text-align': 'center',
        'white-space': 'nowrap',
        background: 'transparent',
        'box-shadow': 'none',
        filter: 'none',
        overflow: 'visible',
      });

      const headingStack = root.querySelector('[class*="ai-reviews-heading-stack"]');
      important(headingStack, {
        display: 'flex',
        flex: '0 0 76px',
        'flex-direction': 'column',
        'align-items': 'center',
        'justify-content': 'flex-start',
        gap: '0',
        width: '100%',
        height: '76px',
        'min-height': '76px',
        margin: '0 auto',
        padding: '0',
        'text-align': 'center',
        'box-sizing': 'border-box',
      });

      const heading = root.querySelector('[class*="ai-reviews-heading-"]:not([class*="stack"])');
      const subtitle = root.querySelector('[class*="ai-reviews-subtitle"]');
      [heading, subtitle].forEach((element) => {
        important(element, {
          display: 'block',
          flex: '0 0 38px',
          color: '#303030',
          'font-family': 'Inter, sans-serif',
          'font-size': '24px',
          'font-weight': '700',
          'letter-spacing': '-0.96px',
          'line-height': '38px',
          height: '38px',
          'min-height': '38px',
          margin: '0 auto',
          padding: '0',
          width: '100%',
          'text-align': 'center',
          overflow: 'visible',
          'box-sizing': 'border-box',
        });
      });

      const carousel = root.querySelector('[class*="ai-reviews-carousel-wrapper"]');
      important(carousel, {
        position: 'relative',
        margin: '0 calc(50% - 50vw) 0',
        padding: '0',
        width: '100vw',
        'max-width': '100vw',
        height: 'auto',
        'min-height': '0',
        overflow: 'visible',
        'box-sizing': 'border-box',
      });

      const track = root.querySelector('[data-reviews-track], [class*="ai-reviews-grid"]');
      important(track, {
        display: 'flex',
        'flex-wrap': 'nowrap',
        'align-items': 'flex-start',
        'justify-content': 'flex-start',
        gap: '24px',
        margin: '0',
        padding: '0',
        width: '100vw',
        'max-width': 'none',
        height: 'auto',
        'min-height': '0',
        'overflow-x': 'auto',
        'overflow-y': 'hidden',
        'scroll-snap-type': 'none',
        'scroll-behavior': 'auto',
        'scrollbar-width': 'none',
        'box-sizing': 'border-box',
      });

      root.querySelectorAll('[data-review-card], article[class*="ai-reviews-card"]').forEach((card) => {
        const lifted = isReviewCardLifted(card);
        important(card, {
          display: 'flex',
          flex: '0 0 288px',
          'flex-direction': 'column',
          'align-items': 'center',
          'justify-content': 'center',
          width: '288px',
          'min-width': '288px',
          'max-width': '288px',
          height: 'auto',
          'min-height': '0',
          'max-height': 'none',
          margin: '0',
          padding: '0',
          border: '0',
          'border-radius': '16px',
          background: '#ffffff',
          'box-shadow': lifted ? reviewCardLiftShadow : reviewCardBaseShadow,
          transform: lifted ? 'translateY(-8px)' : 'translateY(0)',
          transition: 'transform 220ms ease, box-shadow 220ms ease',
          'will-change': 'transform',
          'z-index': lifted ? '3' : 'auto',
          overflow: 'hidden',
          'box-sizing': 'border-box',
          'scroll-snap-align': 'none',
        });
      });

      root.querySelectorAll('[class*="ai-reviews-image"]').forEach((imageWrap) => {
        important(imageWrap, {
          display: 'block',
          flex: '0 0 288px',
          width: '288px',
          height: '288px',
          'min-height': '288px',
          'max-height': '288px',
          margin: '0',
          padding: '0',
          overflow: 'hidden',
          'box-sizing': 'border-box',
        });
        imageWrap.querySelectorAll('img').forEach((img) => {
          important(img, {
            display: 'block',
            width: '288px',
            height: '288px',
            'object-fit': 'cover',
            'object-position': 'center',
            margin: '0',
            padding: '0',
            border: '0',
            'pointer-events': 'none',
          });
        });
      });

      root.querySelectorAll('[class*="ai-reviews-content"]').forEach((content) => {
        important(content, {
          display: 'flex',
          flex: '0 0 auto',
          'flex-direction': 'column',
          'align-items': 'center',
          'justify-content': 'center',
          gap: '4px',
          width: '288px',
          height: 'auto',
          'min-height': '0',
          'max-height': 'none',
          margin: '0',
          padding: '24px',
          background: '#ffffff',
          'border-radius': '0 0 16px 16px',
          'box-shadow': 'none',
          filter: 'none',
          'box-sizing': 'border-box',
          'text-align': 'center',
          overflow: 'hidden',
        });
      });

      root.querySelectorAll('[class*="ai-reviews-text"]').forEach((text) => important(text, reviewTextStyles));
      root.querySelectorAll('[class*="ai-reviews-meta"]').forEach((meta) => {
        important(meta, {
          display: 'flex',
          flex: '0 0 44px',
          'flex-direction': 'column',
          'align-items': 'center',
          'justify-content': 'flex-start',
          gap: '0',
          width: '100%',
          height: '44px',
          'min-height': '44px',
          margin: '0',
          padding: '0',
          'box-sizing': 'border-box',
        });
      });

      root.querySelectorAll('[class*="ai-reviews-author"]').forEach((author) => {
        important(author, {
          display: 'block',
          flex: '0 0 24px',
          width: '100%',
          height: '24px',
          margin: '0',
          padding: '0',
          color: '#424242',
          'font-family': 'Inter, sans-serif',
          'font-size': '16px',
          'font-weight': '500',
          'line-height': '24px',
          'letter-spacing': '-0.32px',
          'text-align': 'center',
          overflow: 'hidden',
        });
      });

      root.querySelectorAll('[class*="ai-reviews-role"]').forEach((role) => {
        important(role, {
          display: 'flex',
          flex: '0 0 20px',
          'align-items': 'center',
          'justify-content': 'center',
          gap: '4px',
          width: '100%',
          height: '20px',
          margin: '0',
          padding: '0',
          color: '#424242',
          'font-family': 'Inter, sans-serif',
          'font-size': '14px',
          'font-weight': '500',
          'line-height': '20px',
          'letter-spacing': '-0.28px',
          'text-align': 'center',
          'white-space': 'nowrap',
          overflow: 'hidden',
        });
      });

      root.querySelectorAll('[class*="ai-reviews-check"]').forEach((check) => {
        important(check, {
          display: 'inline-flex',
          flex: '0 0 16px',
          'align-items': 'center',
          'justify-content': 'center',
          width: '16px',
          height: '16px',
          'min-width': '16px',
          background: 'transparent',
          'border-radius': '0',
          color: '#000000',
        });
        check.querySelectorAll('svg').forEach((svg) => {
          important(svg, {
            display: 'block',
            width: '16px',
            height: '16px',
            overflow: 'visible',
          });
        });
      });

      root.querySelectorAll('[class*="ai-reviews-card-stars"]').forEach((stars) => {
        important(stars, {
          display: 'flex',
          flex: '0 0 24px',
          'align-items': 'center',
          'justify-content': 'center',
          gap: '0',
          width: '120px',
          'min-width': '120px',
          height: '24px',
          'min-height': '24px',
          margin: '0 auto',
          padding: '0',
          color: '#ffbf00',
          background: 'transparent',
          'box-shadow': 'none',
          filter: 'none',
          'line-height': '24px',
          overflow: 'visible',
        });
        stars.querySelectorAll('span').forEach((span) => {
          important(span, {
            display: 'block',
            width: '120px',
            height: '24px',
            margin: '0',
            padding: '0',
            color: '#ffbf00',
            'font-family': 'Inter, sans-serif',
            'font-size': '20px',
            'font-weight': '700',
            'letter-spacing': '1px',
            'line-height': '24px',
            'text-align': 'center',
            'white-space': 'nowrap',
            background: 'transparent',
            'box-shadow': 'none',
            filter: 'none',
          });
        });
        stars.querySelectorAll('svg').forEach((svg) => {
          important(svg, {
            display: 'block',
            flex: '0 0 24px',
            width: '24px',
            height: '24px',
            padding: '3px',
            fill: '#ffbf00',
            background: 'transparent',
            'box-shadow': 'none',
            filter: 'none',
            'box-sizing': 'border-box',
          });
        });
      });

      const controls = root.querySelector('[class*="ai-reviews-controls"]');
      important(controls, {
        display: 'flex',
        'align-items': 'flex-start',
        'justify-content': 'center',
        gap: '16px',
        height: '48px',
        margin: '0',
        padding: '0',
        'box-sizing': 'border-box',
      });
      controls?.querySelectorAll('button').forEach((button) => {
        important(button, {
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'center',
          width: '48px',
          height: '48px',
          margin: '0',
          padding: '12px',
          border: '1.2px solid #dbdbdb',
          'border-radius': '6px',
          background: '#ffffff',
          color: '#303030',
          'box-sizing': 'border-box',
        });
        button.querySelectorAll('svg').forEach((svg) => {
          important(svg, {
            display: 'block',
            width: '24px',
            height: '24px',
          });
        });
      });
    });
  }

  function initSansReviewFigmaInlineLock(scope) {
    applySansReviewFigmaInlineLock(scope || document);
    window.setTimeout(() => applySansReviewFigmaInlineLock(scope || document), 100);
    window.setTimeout(() => applySansReviewFigmaInlineLock(scope || document), 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSansReviewFigmaInlineLock(document), { once: true });
  } else {
    initSansReviewFigmaInlineLock(document);
  }

  document.addEventListener('shopify:section:load', (event) => initSansReviewFigmaInlineLock(event.target));

  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          initSansReviewFigmaInlineLock(document);
          break;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
